import * as THREE from 'three';
import type { LimbRig } from './character-model.js';

export function ease(value: number) {
  const t = THREE.MathUtils.clamp(value, 0, 1);
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/**
 * Authored phase boundaries: anticipation, contact, follow-through and recovery.
 * A monotone cubic runs through the keys, so speed carries through the middle ones instead of stopping at each: a
 * blade passes its keys at full speed and stops only where the values turn (the top of a wind-up, a held pose).
 * The first and last keys are at rest; the curve never overshoots a key, so authored extremes stay the extremes.
 */
export function track(time: number, keys: readonly (readonly [number, number])[]) {
  const last = keys.length - 1;
  if (time <= keys[0][0]) return keys[0][1];
  if (time >= keys[last][0]) return keys[last][1];
  let i = 1;
  while (time > keys[i][0]) i++;
  const [a, from] = keys[i - 1], [b, to] = keys[i], width = b - a, t = (time - a) / width;
  const slope = (j: number) => {
    if (j === 0 || j === last) return 0;
    const before = (keys[j][1] - keys[j - 1][1]) / (keys[j][0] - keys[j - 1][0]), after = (keys[j + 1][1] - keys[j][1]) / (keys[j + 1][0] - keys[j][0]);
    if (before * after <= 0) return 0;
    const wb = 2 * (keys[j + 1][0] - keys[j][0]) + (keys[j][0] - keys[j - 1][0]), wa = (keys[j + 1][0] - keys[j][0]) + 2 * (keys[j][0] - keys[j - 1][0]);
    return (wb + wa) / (wb / before + wa / after);
  };
  const m0 = slope(i - 1) * width, m1 = slope(i) * width, t2 = t * t, t3 = t2 * t;
  return (2 * t3 - 3 * t2 + 1) * from + (t3 - 2 * t2 + t) * m0 + (-2 * t3 + 3 * t2) * to + (t3 - t2) * m1;
}

/** Analytic damped spring; integration does not depend on the display frame rate. */
export class MotionSpring {
  private value?: number;
  private velocity = 0;
  step(target: number, dt: number, frequency = 3.5, damping = .82) {
    if (this.value === undefined) this.value = target;
    if (dt <= 0) return this.value;
    const omega = frequency * Math.PI * 2, decay = damping * omega;
    const damped = omega * Math.sqrt(1 - damping * damping);
    const displacement = this.value - target, oldVelocity = this.velocity;
    const sin = Math.sin(damped * dt), cos = Math.cos(damped * dt), attenuation = Math.exp(-decay * dt);
    this.value = target + attenuation * (displacement * cos + (oldVelocity + decay * displacement) / damped * sin);
    this.velocity = attenuation * (oldVelocity * cos - (decay * oldVelocity + omega * omega * displacement) / damped * sin);
    return this.value;
  }
}

/**
 * How far (rad) a grip leans from square to the forearm toward the forearm's line. A grip runs straight through the closed fist, so it is square:
 * with the wrist straight the weapon stands across the forearm (`holdRigid`).
 */
export const GRIP_TILT = 0;
/** A blade sits in the fist with its edges in line with the knuckles (one toward the target, one back), turned this far about its length. */
export const GRIP_ROLL = Math.PI / 2;
/**
 * Two-bone IK that puts the end of the limb on `target` with the middle joint pointing toward `bendHint`.
 * The middle joint is a hinge: the upper bone is rolled so that the hinge axis is its local X and the lower bone only
 * ever turns about X. A swing-only solve leaves the roll to chance, and the forearm then corkscrews as the target moves.
 * An elbow flexes the forearm forward (negative X); a knee folds the shin back (positive X).
 */
export function solveLimb(limb: LimbRig, target: THREE.Vector3, bendHint: THREE.Vector3, blend = 1, joint: 'elbow' | 'knee' = 'elbow') {
  let scratch = limbSolveScratch.get(limb);
  if (!scratch) { scratch = createLimbSolveScratch(); limbSolveScratch.set(limb, scratch); }
  const { direction, bend, elbow, hand, boneUp, boneLow, side, forward, y, x, upper, lower, basis } = scratch;
  const shoulder = limb.upper.position;
  direction.copy(target).sub(shoulder);
  const upperLength = -limb.lower.position.y, lowerLength = -limb.hand.position.y;
  // A target beyond the limb leaves it straight and short of the mark; the share of its length asked for is kept for the animation fit check.
  if (blend > .25) scratch.demand = Math.max(scratch.demand, direction.length() / (upperLength + lowerLength));
  const distance = THREE.MathUtils.clamp(direction.length(), .01, upperLength + lowerLength - .0001);
  direction.normalize();
  bend.copy(bendHint).addScaledVector(direction, -bendHint.dot(direction)).normalize();
  const along = (upperLength ** 2 - lowerLength ** 2 + distance ** 2) / (2 * distance);
  elbow.copy(shoulder).addScaledVector(direction, along).addScaledVector(bend, Math.sqrt(Math.max(0, upperLength ** 2 - along ** 2)));
  hand.copy(shoulder).addScaledVector(direction, distance);
  boneUp.subVectors(elbow, shoulder).normalize(); boneLow.subVectors(hand, elbow).normalize();
  // The side the hinge opens toward is set by the bend hint: a forearm swings away from the elbow's offset, a shin toward the knee's.
  side.copy(bend).addScaledVector(boneUp, -bend.dot(boneUp));
  if (side.lengthSq() < 1e-8) side.set(0, 0, 1); side.normalize();
  const flex = Math.acos(THREE.MathUtils.clamp(boneLow.dot(boneUp), -1, 1));
  forward.copy(side); if (joint !== 'knee') forward.negate();
  y.copy(boneUp).negate(); x.crossVectors(y, forward);
  upper.setFromRotationMatrix(basis.makeBasis(x, y, forward));
  lower.setFromAxisAngle(LIMB_X_AXIS, joint === 'knee' ? flex : -flex);
  limb.upper.quaternion.slerp(upper, blend); limb.lower.quaternion.slerp(lower, blend);
}

interface LimbSolveScratch {
  direction: THREE.Vector3; bend: THREE.Vector3; elbow: THREE.Vector3; hand: THREE.Vector3;
  boneUp: THREE.Vector3; boneLow: THREE.Vector3; side: THREE.Vector3; forward: THREE.Vector3; y: THREE.Vector3; x: THREE.Vector3;
  upper: THREE.Quaternion; lower: THREE.Quaternion; basis: THREE.Matrix4;
  /** The most of its length the limb was asked to reach (target distance over upper plus lower) since the last `takeLimbReach`. */
  demand: number;
}
const LIMB_X_AXIS = new THREE.Vector3(1, 0, 0);
const limbSolveScratch = new WeakMap<LimbRig, LimbSolveScratch>();
function createLimbSolveScratch(): LimbSolveScratch {
  return {
    direction: new THREE.Vector3(), bend: new THREE.Vector3(), elbow: new THREE.Vector3(), hand: new THREE.Vector3(),
    boneUp: new THREE.Vector3(), boneLow: new THREE.Vector3(), side: new THREE.Vector3(), forward: new THREE.Vector3(),
    y: new THREE.Vector3(), x: new THREE.Vector3(), upper: new THREE.Quaternion(), lower: new THREE.Quaternion(), basis: new THREE.Matrix4(), demand: 0,
  };
}

/** How much of its length `solveLimb` asked of a limb since the last call (1 is fully straight; over 1 the target was out of reach), then starts over. */
export function takeLimbReach(limb: LimbRig) {
  const scratch = limbSolveScratch.get(limb);
  if (!scratch) return 0;
  const demand = scratch.demand; scratch.demand = 0; return demand;
}

